// نمایش تاریخ و ساعت با زبانِ انتخابیِ اپ، نه زبانِ گوشی.
//
// `toLocaleDateString()` بدون آرگومان از زبانِ سیستم‌عامل استفاده می‌کنه. نتیجه‌ش
// این بود که اپ فارسی بود ولی تاریخ‌ها «7/21/2026» نشون داده می‌شدن چون گوشی
// انگلیسی بود. هر جایی که تاریخ نشون داده میشه باید از این‌جا بگذره.
import { days, getLocale, t, type Locale } from './i18n.ts';

/**
 * کدِ زبان → برچسبِ BCP47.
 *
 * برای فارسی عمداً `fa-IR` که تقویم شمسی می‌ده — چیزی که کاربر فارسی‌زبان
 * انتظار داره، نه میلادی با اعداد فارسی.
 *
 * بقیه‌ی نگاشت‌ها چون کدِ خودمون برچسبِ معتبری نیست یا کشور رو لازم داره:
 * `yue` (کانتونی) رو Intl نمی‌شناسه ولی `zh-HK` همون نتیجه رو می‌ده، و
 * تاگالوگ توی Intl اسمش `fil` است نه `tl`.
 */
const TAGS: Partial<Record<Locale, string>> = {
  fa: 'fa-IR', ur: 'ur-PK', pa: 'pa-IN', tl: 'fil-PH', yue: 'zh-HK', sv: 'sv-SE',
};

const tag = (): string => {
  const l = getLocale();
  return TAGS[l] ?? l;
};

/**
 * Intl روی همه‌ی دستگاه‌ها کامل نیست (مخصوصاً Hermes روی اندرویدهای قدیمی‌تر).
 * اگه زبانِ خواسته‌شده پشتیبانی نشه، به پیش‌فرضِ دستگاه برمی‌گردیم — بدتر از
 * ایده‌آله ولی خیلی بهتر از خطا.
 */
function safe(fn: (locale: string) => string): string {
  try {
    return fn(tag());
  } catch {
    try {
      return fn('en');
    } catch {
      return '';
    }
  }
}

/** فقط تاریخ: «۳۱ تیر ۱۴۰۵» یا «Jul 21, 2026» */
export const formatDate = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return safe((l) => d.toLocaleDateString(l, { year: 'numeric', month: 'short', day: 'numeric' }));
};

/**
 * تاریخ بدونِ سال: «۳۱ تیر» یا «Jul 21» - برای تاریخِ رخداد که سالش تقریباً
 * همیشه همینِ امساله و نشون دادنش فقط جا می‌گیره.
 */
export const formatDateShort = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return safe((l) => d.toLocaleDateString(l, { month: 'short', day: 'numeric' }));
};

/** تاریخ و ساعت، برای وقتی که ساعتش هم مهمه. */
export const formatDateTime = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return safe((l) => d.toLocaleString(l, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }));
};

/**
 * دو لحظه، یک روزِ تقویمی‌اند؟ (به وقتِ دستگاه — همون مبنایی که نمایش می‌بینه.)
 *
 * قاعده‌ای که همه‌ی برچسب‌ها لازمش دارن: رخدادی که «۲۵ آگوست ۱۹:۰۰ تا ۲۵ آگوست
 * ۲۱:۰۰»ه، **بازه نیست** — یه شبه. نوشتنش به‌شکلِ «۲۵ آگوست – ۲۵ آگوست» دو بار
 * یه حرفه (۲۶۶ ردیف از داده‌ی امروز دقیقاً همینن).
 */
export const isSameDay = (a?: string | null, b?: string | null): boolean =>
  !!a && !!b && new Date(a).toDateString() === new Date(b).toDateString();

/** فقط ساعت: «۲۰:۳۰». برای وقتی که تاریخ جدا نوشته می‌شه. */
export const formatTime = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return safe((l) => d.toLocaleTimeString(l, { hour: '2-digit', minute: '2-digit' }));
};

/**
 * همون تاریخِ کوتاه، با روزِ هفته‌ی کوتاه‌شده: «شنبه، ۲۴ مرداد» / «Sat, Aug 15».
 *
 * برای کارتِ فشرده‌ی کشف، جایی که «چه روزی از هفته» اولین چیزیه که آدم برای
 * برنامه‌ریزی می‌خواد، ولی جا برای اسمِ کاملِ روز نیست. کوتاه‌شده رو خودِ Intl
 * می‌سازه (برخلافِ `days()` که دستیه و فقط اسمِ کامل داره).
 */
export const formatWeekdayDateShort = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return safe((l) => d.toLocaleDateString(l, {
    weekday: 'short', month: 'short', day: 'numeric',
  }));
};

/**
 * سربرگِ روزهای هفته‌ی تقویم، از روزِ شروعِ همان تقویم (WHOSUP-235).
 * هفت‌تایی و دقیقاً هم‌ترتیبِ خانه‌های شبکه، پس ستون به ستون زیرِ هم می‌افتند.
 *
 * `weekStart` با شماره‌گذاریِ خودِ `Date` است (۰=یکشنبه) — همان چیزی که
 * `weekStartFor` در planBoard می‌دهد.
 *
 * چرا شکلِ **تک‌حرفی** («narrow») و نه کوتاه‌شده: خانه‌ی تقویم روی گوشی حدودِ
 * ۴۵ پیکسله و شکلِ کوتاهِ فارسی همان «چهارشنبه»ی کامله که آن‌جا جا نمی‌شود.
 * تک‌حرف همان قراردادیه که تقویمِ فارسی و انگلیسی هر دو دارند.
 *
 * اگر Intl روی دستگاه ناقص باشد (Hermes روی اندرویدهای قدیمی) به حرفِ اولِ
 * `days()` برمی‌گردیم — که برای فارسی دقیقاً همان تک‌حرف‌هاست. `days()` از
 * شنبه شماره می‌خورد، پس نگاشتش `(dow + 1) % 7` است.
 */
export const weekdayHeads = (weekStart: number): string[] =>
  Array.from({ length: 7 }, (_, i) => {
    const dow = (weekStart + i) % 7;
    // ۴ ژانویه‌ی ۲۰۲۶ یکشنبه است — مبنایی ثابت تا شماره‌ی روز به نامش برسد.
    const d = new Date(2026, 0, 4 + dow);
    const intl = safe((l) => d.toLocaleDateString(l, { weekday: 'narrow' }));
    return intl || Array.from(days()[(dow + 1) % 7] ?? '')[0] || '';
  });

/** روزِ هفته به‌علاوه‌ی تاریخ — برای برنامه‌هایی که «کِی»شون مهم‌تر از «چه تاریخی»ه. */
export const formatWeekdayDate = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return safe((l) => d.toLocaleDateString(l, {
    weekday: 'long', month: 'short', day: 'numeric',
  }));
};

/**
 * روزهای تکرار: «۰=شنبه … ۶=جمعه» → «شنبه‌ها، جمعه‌ها». خالی/نامعتبر یعنی رشته‌ی خالی.
 *
 * سه روزِ پشتِ‌سرِهم و بیشتر به بازه تبدیل می‌شن: «از دوشنبه تا جمعه» به‌جای
 * «دوشنبه، سه‌شنبه، چهارشنبه، پنجشنبه، جمعه». اسمِ روزها کامله (کوتاه‌شده‌شون
 * برای سیزده زبان وجود نداره) و همین برچسبِ پنج‌روزه با بازه‌ی تاریخ کنارش
 * ۶۲ کاراکتر می‌شد؛ روی کارتِ کشف چند خط می‌رفت و مکان و دسته‌ی همون ردیف
 * رو له می‌کرد. دو روز فهرست می‌مونه — «از شنبه تا یکشنبه» نه کوتاه‌تره نه روشن‌تر.
 *
 * بازه با کلمه نوشته می‌شه نه با خط‌تیره: «دوشنبه – جمعه» کنارِ «۱۹ مارس –
 * ۲۷ مارس» یه فهرستِ دوتایی خونده می‌شد، انگار فقط همون دو روز.
 *
 * ورودی مرتب و بی‌تکرار می‌شه، وگرنه [۵،۳،۴] هیچ‌وقت یه بازه دیده نمی‌شد.
 * دورِ هفته بسته نمی‌شه: [جمعه، شنبه] دو تا تکِ جداست، چون «جمعه – شنبه» با
 * ترتیبِ ۰..۶ خونده می‌شه یعنی کلِ هفته.
 */
export const weekdayLabel = (weekdays?: number[] | null): string => {
  const names = days();
  const list = [...new Set((weekdays ?? []).filter((d) => d >= 0 && d <= 6))]
    .sort((a, b) => a - b);

  // هر هفت روز: «شنبه – جمعه» یعنی همون «هر روز»، ولی خواننده باید دو تا اسم
  // رو با ترتیبِ هفته بسنجه تا بفهمه. کلیدش از قبل برای صفحه‌ی در‌دسترس‌بودن
  // ترجمه شده.
  if (list.length === 7) return t('allDays');

  const out: string[] = [];
  for (let i = 0; i < list.length;) {
    let end = i;
    while (end + 1 < list.length && list[end + 1] === list[end] + 1) end++;
    if (end - i >= 2) {
      out.push(t('weekdayRange', { from: names[list[i]], to: names[list[end]] }));
    } else {
      for (let k = i; k <= end; k++) out.push(names[list[k]]);
    }
    i = end + 1;
  }
  return out.join('، ');
};

/**
 * برچسبِ زمانِ یه سیگنالِ تاریخ‌دار (مایگریشن 0057).
 *
 * چهار شکل، از همون سه تصمیمی که فرمِ ساخت می‌گیره:
 *   یه روز، بی‌ساعت      «جمعه، ۳۱ تیر»
 *   یه روز، با ساعت      «جمعه، ۳۱ تیر · ۲۰:۰۰»
 *   بازه، بی‌ساعت        «۳۱ تیر – ۲ مرداد»
 *   بازه، با ساعت        «۳۱ تیر – ۲ مرداد · ۲۰:۰۰»
 *
 * چرا بازه روزِ هفته نمی‌گیره: «جمعه» برای یه بازه‌ی سه‌روزه دروغه، و اسمِ دو
 * روزِ هفته کنارِ دو تا تاریخ سطر رو از چیزی که می‌خونه ردش می‌کنه.
 *
 * ساعت فقط ساعتِ **شروع** است. سیگنال ساعتِ پایان نداره - نگاه کن به
 * توضیحِ بالای مایگریشن 0057.
 */
export const signalWhenLabel = (
  s: { startsAt?: string | null; endsAt?: string | null; dateOnly?: boolean | null },
): string => {
  if (!s.startsAt) return '';
  const time = s.dateOnly ? '' : formatTime(s.startsAt);
  const dates = s.endsAt
    ? `${formatDateShort(s.startsAt)} – ${formatDateShort(s.endsAt)}`
    : formatWeekdayDate(s.startsAt);
  return time ? `${dates} · ${time}` : dates;
};

/**
 * برچسبِ الگوی ماهانه‌ی یه رخداد (مایگریشن 0057). خالی یعنی ماهانه نیست.
 *
 * دو شکل که هر دو ممکنه با هم باشن:
 *   روزمحور   «هر ماه: ۱، ۱۵»
 *   هفته‌محور  «اولین جمعه‌ی هر ماه» — به `weekdays` تکیه داره، چون بدونِ
 *              روزِ هفته «اولینِ» چی؟
 */
export const monthlyLabel = (
  e: { monthdays?: number[] | null; monthWeeks?: number[] | null; weekdays?: number[] | null },
): string => {
  const out: string[] = [];

  const md = (e.monthdays ?? []).filter((d) => d >= 1 && d <= 31);
  if (md.length) out.push(t('monthlyDays', { days: md.join('، ') }));

  const wk = (e.monthWeeks ?? []).filter((w) => w === -1 || (w >= 1 && w <= 5));
  const wd = (e.weekdays ?? []).filter((d) => d >= 0 && d <= 6);
  if (wk.length && wd.length) {
    const names = days();
    out.push(t('monthlyWeek', {
      nth: wk.map((w) => t(ORD_KEY[w] ?? '')).join('، '),
      day: wd.map((d) => names[d]).join('، '),
    }));
  }

  return out.join(' · ');
};

/** کلیدِ ترجمه‌ی «چندمینِ ماه». ‎-1 یعنی آخرین. */
const ORD_KEY: Record<number, string> = {
  1: 'ordFirst', 2: 'ordSecond', 3: 'ordThird', 4: 'ordFourth', 5: 'ordFifth', '-1': 'ordLast',
};

/**
 * برچسبِ تاریخِ کارتِ رخداد - چهار قانون با هم:
 *  ۱. رخدادی که شروع شده دیگه تاریخ شروعش نشون داده نمی‌شه (گذشته‌ست، بی‌فایده‌ست).
 *  ۲. اگه فقط روزهای خاصی از هفته تکرار می‌شه، اون کنارِ تاریخ میاد.
 *  ۳. تاریخ پایان اگه باشه، همیشه نشون داده می‌شه - حتی وقتی شروع مخفیه،
 *     چون «تا کِی ادامه داره» همیشه به‌دردبخوره.
 *  ۴. الگوی ماهانه (مایگریشن 0057) هم مثل روزهای هفته‌ست. وقتی الگو
 *     هفته‌محوره («اولین جمعه‌ی هر ماه») خودش روزِ هفته رو توی خودش داره، پس
 *     `weekdays` دو بار نوشته نمی‌شه.
 */
export const eventDateLabel = (
  e: {
    startsAt?: string | null; endsAt?: string | null; weekdays?: number[] | null;
    monthdays?: number[] | null; monthWeeks?: number[] | null;
  },
  now: Date = new Date(),
): string => {
  const monthly = monthlyLabel(e);
  const nthWeek = !!e.monthWeeks?.length && !!e.weekdays?.length;
  const wd = nthWeek ? '' : weekdayLabel(e.weekdays);
  const started = !!e.startsAt && new Date(e.startsAt).getTime() <= now.getTime();

  // پایانِ هم‌روزِ شروع بازه نیست (نگاه کن به isSameDay)، پس مثلِ یه تک‌تاریخ
  // نوشته می‌شه — با همون قانونِ ۱ هم که می‌گه رخدادِ شروع‌شده تاریخش رو
  // نگه نمی‌داره.
  const ranged = !!e.endsAt && !isSameDay(e.startsAt, e.endsAt);

  let dateText = '';
  if (!started && e.startsAt) {
    dateText = ranged
      ? `${formatDateShort(e.startsAt)} – ${formatDateShort(e.endsAt)}`
      // روزِ هفته فقط کنارِ تک‌تاریخ می‌آد، و فقط وقتی الگوی تکرار خودش روزِ
      // هفته نمی‌گه: «Friday · Sat, Aug 15» هم شلوغه هم متناقض به نظر می‌رسه.
      // بازه هم نمی‌گیردش: دو تا اسمِ روز کنارِ دو تا تاریخ، سطرِ کارت رو
      // از چیزی که می‌خونه ردش می‌کنه (همون دلیلِ weekdayLabel).
      : wd ? formatDateShort(e.startsAt) : formatWeekdayDateShort(e.startsAt);
  } else if (ranged) {
    dateText = `→ ${formatDateShort(e.endsAt)}`;
  }

  return [wd, monthly, dateText].filter(Boolean).join(' · ');
};

/** تاریخ کارت عکس‌کوچک؛ ساعت فقط وقتی می‌آید که همه‌ی اجراها ساعت شروع یکسانی دارند. */
export const eventThumbnailDateLabel = (
  e: Parameters<typeof eventDateLabel>[0] & { occurrenceStartsAt?: string[] },
  now: Date = new Date(),
): string => {
  const dateText = eventDateLabel(e, now);
  if (!dateText || !e.startsAt) return dateText;

  const times = [e.startsAt, ...(e.occurrenceStartsAt ?? [])]
    .map(formatTime)
    .filter(Boolean);
  const time = new Set(times).size === 1 ? times[0] : '';
  return [dateText, time].filter(Boolean).join(' · ');
};
